import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { EMPTY, Observable, of } from 'rxjs';
import { catchError, concatMap, map, switchMap } from 'rxjs/operators';
import { CartAccountingCodesFacade } from '../../../../features/cart/base';
import { LoginLogoutActionTypes } from '../../../auth/user-auth/store/actions/login-logout.action';
import { GlobalMessageType, isErrorGloballyHandled } from '../../../global-message';
import { HttpResponseStatus } from '../../../global-message/models/response-status.model';
import { GlobalMessageActions } from '../../../global-message/store';
import { AccountingCode, AccountingCodeUserInfo } from '../../../model';
import { CurrenciesActionTypes } from '../../../site-context/store/actions/currencies.action';
import { LanguagesActionTypes } from '../../../site-context/store/actions/languages.action';
import { normalizeHttpError, withdrawOn } from '../../../util';
import { AppActions } from '../../../util/store/actions';
import { NoOpAction } from '../../../util/store/actions/app.action';
import { AccountingCodesAdminConnector } from '../../connectors/accounting-codes-admin-connector';
import { AccountingCodesAdminFacade } from '../../facades';
import { UserActions } from '../actions';

@Injectable()
export class AccountingCodesAdminEffects {
  private contextChange$ = this.actions$.pipe(
    ofType(CurrenciesActionTypes.CurrencyChange, LanguagesActionTypes.LanguageChange, LoginLogoutActionTypes.Logout)
  );
  private logoutOnlyContextChange$ = this.actions$.pipe(ofType(LoginLogoutActionTypes.Logout));

  searchAccountingCodesAdmin$: Observable<
    UserActions.SearchAccountingCodesAdminSuccess | UserActions.SearchAccountingCodesAdminFail
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.AccountingCodesAdminActionTypes.SearchAccountingCodesAdmin),
      map((action: UserActions.SearchAccountingCodesAdmin) => action.payload),
      concatMap((payload) =>
        this.connector.search(payload.userId, payload.searchParams).pipe(
          map((data) => new UserActions.SearchAccountingCodesAdminSuccess({ ...payload, searchResult: data })),
          catchError((error) =>
            of(
              new UserActions.SearchAccountingCodesAdminFail({
                ...payload,
                error: normalizeHttpError(error),
              })
            )
          )
        )
      ),
      withdrawOn(this.contextChange$)
    )
  );

  createAccountingCodeAdmin$: Observable<
    UserActions.CreateAccountingCodeAdminSuccess | UserActions.CreateAccountingCodeAdminFail | GlobalMessageActions.AddMessage
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.AccountingCodesAdminActionTypes.CreateAccountingCodeAdmin),
      map((action: UserActions.CreateAccountingCodeAdmin) => action.payload),
      switchMap((payload) =>
        this.connector.create(payload.userId, payload.accountingCode).pipe(
          concatMap((accountingCode: AccountingCode) => [
            new UserActions.CreateAccountingCodeAdminSuccess({ ...payload, accountingCode }),
            new GlobalMessageActions.AddMessage({
              text: { key: 'accountingCodes.createAccountingCodeSuccess_message' },
              type: GlobalMessageType.MSG_TYPE_CONFIRMATION,
            }),
          ]),
          catchError((error) =>
            of(
              new UserActions.CreateAccountingCodeAdminFail({
                ...payload,
                error: normalizeHttpError(error),
              })
            )
          )
        )
      ),
      withdrawOn(this.logoutOnlyContextChange$)
    )
  );

  handleSuccessForCreateAccountingCodeAdmin$: Observable<NoOpAction> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActions.AccountingCodesAdminActionTypes.CreateAccountingCodeAdminSuccess),

        map((action: UserActions.CreateAccountingCodeAdminSuccess) => action.payload),
        map((payload) => {
          this.accountingCodesAdminService.search(payload.searchParams);
          this.cartAccountingCodesService.loadAccountingCodeList();
          return new AppActions.NoOpAction();
        })
      ),
    { dispatch: false }
  );

  handleFailureForCreateAccountingCodeAdmin$: Observable<GlobalMessageActions.AddMessage | AppActions.NoOpAction> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActions.AccountingCodesAdminActionTypes.CreateAccountingCodeAdminFail),
        map((action: UserActions.CreateAccountingCodeAdminFail) => {
          if (!isErrorGloballyHandled(action.payload.error?.status)) {
            return new GlobalMessageActions.AddMessage({
              text: {
                key: 'accountingCodes.createAccountingCodeFailure_message',
              },
              type: GlobalMessageType.MSG_TYPE_ERROR,
            });
          } else {
            return new AppActions.NoOpAction();
          }
        })
      )
  );

  updateAccountingCodeAdmin$: Observable<
    UserActions.UpdateAccountingCodeAdminSuccess | UserActions.UpdateAccountingCodeAdminFail
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.AccountingCodesAdminActionTypes.UpdateAccountingCodeAdmin),
      map((action: UserActions.UpdateAccountingCodeAdmin) => action.payload),
      switchMap((payload) =>
        this.connector.update(payload.userId, payload.accountingCode).pipe(
          map(
            (accountingCode: AccountingCode) =>
              new UserActions.UpdateAccountingCodeAdminSuccess({
                ...payload,
                accountingCode,
              })
          ),
          catchError((error) =>
            of(
              new UserActions.UpdateAccountingCodeAdminFail({
                ...payload,
                error: normalizeHttpError(error),
              })
            )
          )
        )
      ),
      withdrawOn(this.logoutOnlyContextChange$)
    )
  );

  handleFailureForUpdateAccountingCodeAdmin$: Observable<GlobalMessageActions.AddMessage | AppActions.NoOpAction> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActions.AccountingCodesAdminActionTypes.UpdateAccountingCodeAdminFail),
        map((action: UserActions.UpdateAccountingCodeAdminFail) => {
          if (!isErrorGloballyHandled(action.payload.error?.status)) {
            if (action.payload.error?.status === HttpResponseStatus.NOT_FOUND) {
              return new GlobalMessageActions.AddMessage({
                text: {
                  key: 'accountingCodes.accountingCodeNotFound_message',
                  params: {
                    accountingCodeCode: action.payload.accountingCode.code,
                  },
                },
                type: GlobalMessageType.MSG_TYPE_ERROR,
              });
            } else {
              return new GlobalMessageActions.AddMessage({
                text: {
                  key: 'accountingCodes.accountingCodeUpdateFailure_message',
                  params: {
                    accountingCodeCode: action.payload.accountingCode.code,
                  },
                },
                type: GlobalMessageType.MSG_TYPE_ERROR,
              });
            }
          } else {
            return new AppActions.NoOpAction();
          }
        })
      )
  );

  deleteAccountingCodeAdmin$: Observable<
    UserActions.DeleteAccountingCodeAdminSuccess | UserActions.DeleteAccountingCodeAdminFail
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.AccountingCodesAdminActionTypes.DeleteAccountingCodeAdmin),
      map((action: UserActions.DeleteAccountingCodeAdmin) => action.payload),
      switchMap((payload) =>
        this.connector.delete(payload.userId, payload.code).pipe(
          map(() => new UserActions.DeleteAccountingCodeAdminSuccess(payload)),
          catchError((error) =>
            of(
              new UserActions.DeleteAccountingCodeAdminFail({
                ...payload,
                error: normalizeHttpError(error),
              })
            )
          )
        )
      ),
      withdrawOn(this.logoutOnlyContextChange$)
    )
  );

  handleFailureForDeleteAccountingCodeAdmin$: Observable<GlobalMessageActions.AddMessage | AppActions.NoOpAction> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActions.AccountingCodesAdminActionTypes.DeleteAccountingCodeAdminFail),
        map((action: UserActions.DeleteAccountingCodeAdminFail) => {
          if (!isErrorGloballyHandled(action.payload.error?.status)) {
            return new GlobalMessageActions.AddMessage({
              text: {
                raw: action.payload.error?.details[0].message,
              },
              type: GlobalMessageType.MSG_TYPE_ERROR,
            });
          } else {
            return new AppActions.NoOpAction();
          }
        })
      )
  );

  handleSuccessForDeleteAccountingCodeAdmin$: Observable<GlobalMessageActions.AddMessage | AppActions.NoOpAction> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActions.AccountingCodesAdminActionTypes.DeleteAccountingCodeAdminSuccess),
        map((action: UserActions.DeleteAccountingCodeAdminSuccess) => action.payload),
        map((payload) => {
          this.accountingCodesAdminService.search(payload.searchParams);
          this.cartAccountingCodesService.loadAccountingCodeList();
          return new AppActions.NoOpAction();
        }),
        switchMap(() => [
          new GlobalMessageActions.AddMessage({
            text: {
              key: 'accountingCodes.accountingCodeRemovalSuccess_message',
            },
            type: GlobalMessageType.MSG_TYPE_CONFIRMATION,
          }),
          new AppActions.NoOpAction(),
        ])
      )
  );

  loadAccountingCodeUserInfo$: Observable<UserActions.LoadAccountingCodeUserInfoSuccess> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.AccountingCodesAdminActionTypes.LoadAccountingCodeUserInfo),
      map((action: UserActions.LoadAccountingCodeUserInfo) => action.payload),
      switchMap((userId) => {
        return this.connector.load(userId).pipe(
          map(
            (accountingCodeUserInfo: AccountingCodeUserInfo) =>
              new UserActions.LoadAccountingCodeUserInfoSuccess(accountingCodeUserInfo)
          ),
          catchError((_) => EMPTY)
        );
      }),
      withdrawOn(this.logoutOnlyContextChange$)
    )
  );

  constructor(
    private actions$: Actions,
    private connector: AccountingCodesAdminConnector,
    private accountingCodesAdminService: AccountingCodesAdminFacade,
    private cartAccountingCodesService: CartAccountingCodesFacade
  ) {}
}
